import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  IconButton,
  TextField,
  Divider,
  FormControl,
  Select,
  MenuItem,
  Avatar,
} from "@mui/material";
import {
  RemoveCircle,
  AddCircle,
  Delete,
  ShoppingCart,
  ArrowLeft,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { showToast } from "@/app/global-components/toaster";
import { routes } from "@/consts";
import { useCart } from "@/contexts/CartContext";
// import useDirectOrder from "@/hooks/use-direct-order";
import { useShipping } from "@/hooks/use-shipping";
// import { FileUploader } from "react-drag-drop-files";
import { motion } from "framer-motion";
import {
  CreditCardIcon,
  DollarSign,
  MapPin,
  Package,
  // ReceiptCent,
} from "lucide-react";
import useOrder from "@/hooks/use-order";
import { useSelector } from "react-redux";
import { RootState } from "@/store/type";
import useProfile from "@/hooks/use-profile";
import { getMyCompany } from "@/store/authSlice";
import { useDispatch } from "react-redux";

// const bankInfo = {
//   accountName: "شركة ميدا المتقدمة لحلول الاعمال",
//   accountNumber: "212000010006086127973",
//   iban: "SA0780000212608016127973",
//   bankName: "بنك الراجحي",
// };

const CheckoutPage: React.FC = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const { cartItems, updateQuantity, removeFromCart, clearCart } = useCart();

  const { myShipping } = useShipping();
  const navigate = useNavigate();
  const { createOrder } = useOrder();

  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [addresses, setAddresses] = useState<any[]>([]);
  const [selectedAddress, setSelectedAddress] = useState<string>("");
  const myCompany = useSelector((state: RootState) => state.auth.myCompany);
  const { getUserProfile } = useProfile();
  useEffect(() => {
    fetchAddresses();
  }, []);
  const dispatch = useDispatch();
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!myCompany?.id) {
        try {
          const userData = await getUserProfile();
          if (userData.data.company) {
            dispatch(getMyCompany(userData.data.company));
          }
        } catch (error) {
          console.error("Error fetching user profile:", error);
        }
      }
    };
    fetchUserProfile();
  }, []);
  const fetchAddresses = async () => {
    try {
      const data = await myShipping();
      if (data) {
        setAddresses(data);
        const defaultAddress = data.find((addr: any) => addr.isDefault);
        if (defaultAddress) {
          setSelectedAddress(defaultAddress.id);
        }
      }
    } catch (error) {
      console.error("Error fetching addresses:", error);
    }
  };

  const handleQuantityChange = (id: string, quantity: number) => {
    if (quantity < 1) return;
    updateQuantity(id, quantity);
  };

  const handleRemoveItem = (productId: string) => {
    removeFromCart(productId);
  };
  const authUser = useSelector((state: RootState) => state.auth.authUser);

  const handlePlaceOrder = async () => {
    if (cartItems.length === 0) {
      showToast(t("Alert.CartEmpty"), "warning");
      return;
    }

    if (!selectedAddress) {
      showToast("Please select a shipping address", "warning");
      return;
    }

    setIsPlacingOrder(true);
    try {
      const selectedAddressData = addresses.find(
        (addr) => addr.id === selectedAddress
      );
      if (!selectedAddressData) {
        throw new Error("Selected address not found");
      }
      // fetch myCompany
      const products = cartItems.map((item) => ({
        item: item.name,
        itemId: item.id,
        image: item.images[0],
        companyId: item.companyId,
        quantity: item.quantity,
        price: item.price,
      }));
      const orderData = {
        status: "Planning",
        userId: authUser?.id,
        products,
        region: myCompany?.region,
        city: myCompany?.city,
        address: myCompany?.address,
        companyId: myCompany?.id,
        title: "Medical Equipment Order",
        deliverDate: new Date(new Date().setDate(new Date().getDate() + 1)),
        type: "Huge",
        Sendedproducts: [],
        OrderNr: myCompany ? myCompany.orderNr + 1 : 1,
      };
      const result = await createOrder(orderData);
      if (result.isSuccess) {
        showToast(t("Alert.OrderCreated"), "success");
        clearCart();
        navigate(`${routes.order}/${result.data.id}`);
      } else {
        showToast(t("Alert.OrderCreationError"), "warning");
      }
    } catch (error) {
      showToast(t("Alert.OrderTryAgain"), "warning");
      console.error("Error creating orders:", error);
    } finally {
      setIsPlacingOrder(false);
      // setIsInvoiceModalOpen(false);
    }
  };

  const calculateTotal = () =>
    cartItems.reduce(
      (acc, item) =>
        acc +
        item.price * item.quantity +
        (item.price * item.quantity * 15) / 100,
      0
    );

  const calculateTotalVAT = () =>
    cartItems.reduce(
      (acc, item) => acc + (item.price * item.quantity * 15) / 100,
      0
    );

  const calculateSubtotal = () =>
    cartItems.reduce((acc, item) => acc + item.price * item.quantity, 0);

  if (cartItems.length === 0) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background: "#f8fafc",
          padding: {
            xs: "12px 16px",
            sm: "16px 20px",
            md: "20px 24px",
            lg: "24px 32px",
          },
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md mx-auto"
        >
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <ShoppingCart className="w-10 h-10 text-gray-400" />
          </div>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, color: "#1f2937", mb: 2 }}
          >
            {t("checkout.reviewCart")}
          </Typography>
          <Typography variant="body1" sx={{ color: "#6b7280", mb: 4 }}>
            {t("checkout.messages.emptyCart")}
          </Typography>
          <Button
            onClick={() => navigate(routes.marketPlace)}
            className="!bg-zinc-900 !text-white px-6 py-3 rounded-xl font-medium transition-all duration-200"
          >
            {t("buyer_pages.cart.continue")}
          </Button>
        </motion.div>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "#f8fafc",
        padding: {
          xs: "12px 16px",
          sm: "16px 20px",
          md: "20px 24px",
          lg: "24px 32px",
        },
      }}
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center gap-3 mb-8"
      >
        <Button
          variant="outlined"
          onClick={() => navigate(-1)}
          className="border-gray-300 text-gray-600 hover:bg-gray-50"
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <Typography variant="h3" sx={{ fontWeight: 700, color: "#1f2937" }}>
            {t("checkout.reviewCart")}
          </Typography>
          <Typography variant="body1" sx={{ color: "#6b7280", mt: 1 }}>
            {cartItems.length} {cartItems.length === 1 ? "item" : "items"} in
            your cart
          </Typography>
        </div>
      </motion.div>

      <Grid container spacing={4}>
        {/* Cart Items */}
        <Grid item xs={12} lg={8}>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card sx={{ borderRadius: 2, border: 0, mb: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}
                >
                  <Avatar sx={{ bgcolor: "#00990033" }}>
                    <Package size={20} className="text-green-600" />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t("checkout.reviewCart")}
                  </Typography>
                </Box>

                <div className="space-y-4">
                  {cartItems.map((item, index) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                    >
                      <Card sx={{ borderRadius: 2, border: 0, p: 3 }}>
                        <Box
                          sx={{ display: "flex", gap: 3, alignItems: "center" }}
                        >
                          {/* Product Image */}
                          <CardMedia
                            component="img"
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: 2,
                              objectFit: "cover",
                            }}
                            image={item.images[0] || "/placeholder-image.png"}
                            alt={item?.name}
                          />

                          {/* Product Details */}
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="h6"
                              sx={{ fontWeight: 600, mb: 1 }}
                            >
                              {item?.name}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                mb: 1,
                              }}
                            >
                              <Package className="w-4 h-4 text-gray-500" />
                              <Typography
                                variant="body2"
                                sx={{ color: "#6b7280" }}
                              >
                                {item?.store?.name}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                mb: 2,
                              }}
                            >
                              <DollarSign className="w-4 h-4 text-emerald-600" />
                              <Typography
                                variant="h6"
                                sx={{ color: "#16a34a", fontWeight: 600 }}
                              >
                                {item.price.toFixed(2)} {t("currency")}
                              </Typography>
                            </Box>

                            {/* Quantity Controls */}
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{ color: "#6b7280", fontWeight: 500 }}
                              >
                                {t("checkout.fields.quantity")}:
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleQuantityChange(
                                    item.id,
                                    item.quantity - 1
                                  )
                                }
                                disabled={item.quantity <= item.minOrder}
                                sx={{
                                  color: "#644980",
                                  "&:disabled": { color: "#9ca3af" },
                                }}
                              >
                                <RemoveCircle />
                              </IconButton>
                              <TextField
                                value={item.quantity}
                                size="small"
                                variant="outlined"
                                sx={{ width: 60 }}
                                inputProps={{
                                  style: { textAlign: "center" },
                                  min: item.minOrder,
                                }}
                                onChange={(e) =>
                                  handleQuantityChange(
                                    item.id,
                                    parseInt(e.target.value) || item.minOrder
                                  )
                                }
                              />
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleQuantityChange(
                                    item.id,
                                    item.quantity + 1
                                  )
                                }
                                sx={{ color: "#644980" }}
                              >
                                <AddCircle />
                              </IconButton>
                            </Box>
                          </Box>

                          {/* Remove Button */}
                          <IconButton
                            onClick={() => handleRemoveItem(item.id)}
                            sx={{
                              color: "#ef4444",
                              "&:hover": { backgroundColor: "#fef2f2" },
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </Box>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        {/* Order Summary */}
        <Grid item xs={12} lg={4}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card
              sx={{
                borderRadius: 2,
                border: 0,
                position: "sticky",
                top: 24,
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}
                >
                  <Avatar sx={{ bgcolor: "#00009911" }}>
                    <CreditCardIcon className="text-blue-600" size={20} />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {t("checkout.summary.title")}
                  </Typography>
                </Box>

                {/* Shipping Address Selection */}
                <Box sx={{ mb: 4 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <MapPin className="w-4 h-4 text-blue-600" />
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#374151" }}
                    >
                      {t("checkout.fields.shippingAddress")}
                    </Typography>
                  </Box>
                  <FormControl fullWidth>
                    <Select
                      value={selectedAddress}
                      onChange={(e) => setSelectedAddress(e.target.value)}
                      sx={{ borderRadius: 2 }}
                    >
                      {addresses.map((address) => (
                        <MenuItem key={address.id} value={address.id}>
                          {address.fullName} - {address.address}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Price Breakdown */}
                <Box sx={{ spaceY: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                      {t("checkout.summary.totalPrice")}:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {calculateSubtotal().toFixed(2)} {t("currency")}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                      {t("checkout.summary.totalVat")}:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {calculateTotalVAT().toFixed(2)} {t("currency")}
                    </Typography>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 3,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: "#1f2937" }}
                    >
                      {t("checkout.summary.grandTotal")}:
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, color: "#644980" }}
                    >
                      {calculateTotal().toFixed(2)} {t("currency")}
                    </Typography>
                  </Box>
                </Box>

                <Button
                  fullWidth
                  onClick={handlePlaceOrder}
                  disabled={
                    cartItems.length === 0 || isPlacingOrder || !selectedAddress
                  }
                  sx={{
                    background: "#18181b",
                    color: "white",
                    py: 2,
                    borderRadius: 2,
                    fontWeight: 600,

                    "&:disabled": {
                      background: "#e5e7eb",
                      color: "#9ca3af",
                    },
                  }}
                >
                  {isPlacingOrder
                    ? t("actions.creatingOrder")
                    : t("actions.createOrder")}
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CheckoutPage;
